import { useEffect, useId, useState } from "react";
import PropTypes from "prop-types";

import "../../styles/RatingProgressBar.scss";

const RatingProgressBar = ({ countObject, label = "Ratings Entered" }) => {
    const [isCompact, setIsCompact] = useState(false);
    const id = useId();

    const gradientId = `barGradientFull-${id}`;
    const clipId = `barClip-${id}`;

    const rated = Number(countObject?.ratedCount ?? 0);
    const total = Number(countObject?.totalCount ?? 0);
    const pct = total > 0 ? Math.min(100, Math.max(0, (rated / total) * 100)) : 0;
    const pctRounded = Math.round(pct);

    const pctClean = Math.max(0, Math.min(100, pct));

    const color1 = "#C8E6C9";
    const color2 = "#81C784";
    const color3 = "#4CAF50";
    const color4 = "#388E3C";
    const color5 = "#1B5E20";

    /**
     * Checks screen size to switch to tooltip when screen width is <=1300px
     */
    useEffect(() => {
        const handleResize = () => {
            setIsCompact(window.innerWidth <= 950);
        };

        handleResize(); // initial check
        window.addEventListener("resize", handleResize);
        return () => window.removeEventListener("resize", handleResize);
    }, []);

    return (
        <div className="rating-gauge">
            {!isCompact && (
                <div className="bar-wrap" role="img" aria-label={`${pctRounded}% complete`}>
                    <svg viewBox="0 0 100 26" className="bar-svg" focusable="false" aria-hidden="true">
                        <defs>
                            <linearGradient
                                id={gradientId}
                                x1="0"
                                y1="0"
                                x2="100"
                                y2="0"
                                gradientUnits="userSpaceOnUse"
                                spreadMethod="pad"
                            >
                                <stop offset="0%" stopColor={color1} />
                                <stop offset="20%" stopColor={color2} />
                                <stop offset="45%" stopColor={color3} />
                                <stop offset="75%" stopColor={color4} />
                                <stop offset="100%" stopColor={color5} />
                            </linearGradient>

                            <clipPath id={clipId}>
                                <rect x="0" y="6" width="100" height="10" rx="5" ry="5" />
                            </clipPath>
                        </defs>

                        {/* Gray base track */}
                        <g clipPath={`url(#${clipId})`}>
                            <rect x="0" y="6" width="100" height="10" fill="#e6e6e6" />
                        </g>

                        {/* Gradient fill up to pct */}
                        <g clipPath={`url(#${clipId})`}>
                            <rect x="0" y="6" width={pctClean} height="10" fill={`url(#${gradientId})`} />
                        </g>

                        {/* Marker + % */}
                        <line x1={pctClean} x2={pctClean} y1="6" y2="16" className="bar-needle" />
                        <circle cx={pctClean} cy="11" r="2.5" className="bar-hub" />
                    </svg>
                </div>
            )}
            <div className="gauge-text">
                <strong>{rated}</strong> of <strong>{total}</strong> {label}
            </div>
        </div>
    );
};

RatingProgressBar.propTypes = {
    countObject: PropTypes.shape({
        ratedCount: PropTypes.number.isRequired,
        totalCount: PropTypes.number.isRequired
    }).isRequired,
    label: PropTypes.string
};

export default RatingProgressBar;
